/**
 * How the visit page writes the server's values — Slice 1's shared helpers
 * (lead decisions, round 1 item 2; `docs/visit-page.md` §3.2).
 *
 * **Formatting only.** The server computes every number (§2.1) and sends
 * money as decimal strings at the precision the board prints (`"8888.07"`,
 * `"8888"`) and differences already signed (`Delta`). What is left for the
 * page is the `$`, the thousands commas, the typographic minus, the unit, and
 * dates and times in the visit's own zone — nothing here adds, subtracts,
 * rounds money or compares two figures.
 *
 * No words live here: every word is in `copy.ts`. Date patterns are patterns.
 */
import { formatInTimeZone } from 'date-fns-tz'
import type { Delta, Hours, IsoDay, IsoMoment, LocalTime, Minutes } from '@rowted/shared'
import { formatMoneyText } from '../lib/format.js'

/** The boards' minus sign (−), never a hyphen. */
const MINUS = '−'

/** `"8888.07"` → *$8,888.07*; `"-58.40"` → *−$58.40*. */
export function money(value: string): string {
  return formatMoneyText(value)
}

/** A signed money difference — `"+19.47"` → *+$19.47*, `"-41.25"` → *−$41.25*, zero → *$0.00*. */
export function signedMoney(delta: Delta | string): string {
  const signed = typeof delta === 'string' ? delta : delta.signed
  if (signed.startsWith('+')) return `+${formatMoneyText(signed.slice(1))}`
  return formatMoneyText(signed)
}

/** A signed number of the same kind — hours `"+1.0"` → *+1.0*, points `"-0.2"` → *−0.2*. */
export function signed(delta: Delta | string): string {
  const value = typeof delta === 'string' ? delta : delta.signed
  return value.startsWith('-') ? `${MINUS}${value.slice(1)}` : value
}

/** Hours to the one decimal the board prints — *35.9*, *76.0*, *28.0*. */
export function hours1(value: Hours): string {
  return value.toFixed(1)
}

/**
 * Hours as a planned figure the board prints bare — *76* (*of 76 so far*),
 * *22* (*22 h planned*), *6.5*. The server sends the number; a whole one has
 * no decimal.
 */
export function hoursBare(value: Hours): string {
  return String(value)
}

/** A percent as drawn — *37.6%*. */
export function percent(value: number): string {
  return `${value.toFixed(1)}%`
}

/** Minutes as *h:mm* — 625 → *10:25* (on the clock, §6.5). */
export function hm(minutes: Minutes): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

/**
 * A moment in the visit's own zone (the branch office's, `VisitRead.timeZone`):
 *
 * - `clock` *6:31* — the boards' times inside a day (*on site since 6:58*, *Marco Diaz, 6:45*);
 * - `ampm` *1:18 PM*;
 * - `day` *Wed*;
 * - `dayClock` *Fri 6:45*;
 * - `dayAmpm` *Wed 1:18 PM*;
 * - `date` *Wed Oct 21*;
 * - `dateAmpm` *Wed Oct 21, 1:18 PM*;
 * - `monthDay` *Oct 16*;
 * - `hourAmpm` *2 PM* (*about 2 PM*).
 */
export type MomentStyle = 'clock' | 'ampm' | 'day' | 'dayClock' | 'dayAmpm' | 'date' | 'dateAmpm' | 'monthDay' | 'hourAmpm'

export function timeIn(timeZone: string, iso: IsoMoment, style: MomentStyle): string {
  const at = new Date(iso)
  switch (style) {
    case 'clock': return formatInTimeZone(at, timeZone, 'h:mm')
    case 'ampm': return formatInTimeZone(at, timeZone, 'h:mm a')
    case 'day': return formatInTimeZone(at, timeZone, 'EEE')
    case 'dayClock': return formatInTimeZone(at, timeZone, 'EEE h:mm')
    case 'dayAmpm': return formatInTimeZone(at, timeZone, 'EEE h:mm a')
    case 'date': return formatInTimeZone(at, timeZone, 'EEE MMM d')
    case 'dateAmpm': return formatInTimeZone(at, timeZone, 'EEE MMM d, h:mm a')
    case 'monthDay': return formatInTimeZone(at, timeZone, 'MMM d')
    case 'hourAmpm': {
      const minutes = formatInTimeZone(at, timeZone, 'mm')
      return formatInTimeZone(at, timeZone, minutes === '00' ? 'h a' : 'h:mm a')
    }
  }
}

/** A planned time of day `HH:MM` as the boards print it — `07:00` → *7:00*, `16:30` → *4:30*. */
export function localTime(value: LocalTime): string {
  const [hh = '0', mm = '00'] = value.split(':')
  const hour = Number(hh) % 12 || 12
  return `${hour}:${mm}`
}

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** A calendar day's parts, read as text (a `YYYY-MM-DD` is no instant). */
function partsOf(day: IsoDay): { year: number; month: number; date: number; weekday: number } {
  const [y = '1970', m = '01', d = '01'] = day.split('-')
  const year = Number(y)
  const month = Number(m) - 1
  const date = Number(d)
  return { year, month, date, weekday: new Date(Date.UTC(year, month, date)).getUTCDay() }
}

/**
 * A calendar day:
 *
 * - `date` *Wed Oct 21*;
 * - `monthDay` *Oct 21*, *Apr 15*;
 * - `day` *Wed*;
 * - `weekday` *Friday* (*Ready for Friday*);
 * - `dateYear` *Wed Oct 21, 2026*.
 */
export type DayStyle = 'date' | 'monthDay' | 'day' | 'weekday' | 'dateYear'

export function dayText(day: IsoDay, style: DayStyle): string {
  const { year, month, date, weekday } = partsOf(day)
  const short = WEEKDAYS_SHORT[weekday] ?? ''
  const mon = MONTHS_SHORT[month] ?? ''
  switch (style) {
    case 'date': return `${short} ${mon} ${date}`
    case 'monthDay': return `${mon} ${date}`
    case 'day': return short
    case 'weekday': return WEEKDAYS[weekday] ?? ''
    case 'dateYear': return `${short} ${mon} ${date}, ${year}`
  }
}

/** The weekday a series runs on, 0 Sunday … 6 Saturday → *Thursday*. */
export function weekdayName(weekday: number): string {
  return WEEKDAYS[weekday] ?? ''
}

/**
 * Two days as one span — `long` *Wed Oct 21 – Fri Oct 23*; `short` *Oct 21 –
 * 23* in one month, *Oct 30 – Nov 2* across two (the rail's *This visit* line,
 * Board 1).
 */
export function dayRange(first: IsoDay, last: IsoDay, style: 'long' | 'short' | 'monthDay'): string {
  if (style === 'long') return `${dayText(first, 'date')} – ${dayText(last, 'date')}`
  if (style === 'monthDay') return `${dayText(first, 'monthDay')} – ${dayText(last, 'monthDay')}`
  const a = partsOf(first)
  const b = partsOf(last)
  return a.month === b.month
    ? `${dayText(first, 'monthDay')} – ${b.date}`
    : `${dayText(first, 'monthDay')} – ${dayText(last, 'monthDay')}`
}

/** The visit mark's band — *OCT*. */
export function monthBand(day: IsoDay): string {
  return (MONTHS_SHORT[partsOf(day).month] ?? '').toUpperCase()
}

/** The visit mark's day number — *21*. */
export function dayNumber(day: IsoDay): string {
  return String(partsOf(day).date)
}

/** A `tel:` link from a phone number as typed. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`
}

/** An `sms:` link from a phone number as typed. */
export function smsHref(phone: string): string {
  return `sms:${phone.replace(/[^\d+]/g, '')}`
}
